import { createContext, useState, useEffect, useContext } from "react";
import { jwtDecode } from "jwt-decode"; // ✅ 필수
import { useNavigate } from "react-router-dom";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const Server_IP = "";
  const [token, setToken] = useState(null);

  const getPersistedValue = (key, fallback = null) => {
    return sessionStorage.getItem(key) || localStorage.getItem(key) || fallback;
  };

  const setPersistedValue = (key, value) => {
    if (value) {
      sessionStorage.setItem(key, value);
      localStorage.setItem(key, value);
    } else {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
    }
  };

  const [userId, _setUserId] = useState(() => getPersistedValue("userId"));
  const [userType, _setUserType] = useState(() =>
    getPersistedValue("userType", "client"),
  ); // "manager" = 관리계정(친우메드),   "cliient" = 거래처
  const [managerName, _setManagerName] = useState(() =>
    getPersistedValue("managerName", ""),
  ); //관리자 로그인시 직원 이름
  const [managerEmail, _setManagerEmail] = useState(() =>
    getPersistedValue("managerEmail", ""),
  ); //관리자 로그인시 직원 이메일

  const setUserId = (val) => {
    setPersistedValue("userId", val);
    _setUserId(val);
  };

  const setUserType = (val) => {
    setPersistedValue("userType", val);
    _setUserType(val);
  };

  const setManagerName = (val) => {
    setPersistedValue("managerName", val);
    _setManagerName(val);
  };

  const setManagerEmail = (val) => {
    setPersistedValue("managerEmail", val);
    _setManagerEmail(val);
  };
  const [expiryTimeoutId, setExpiryTimeoutId] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true); // ✅ 초기화 상태 추가
  const [showExpiryModal, setShowExpiryModal] = useState(false); // ✅ 모달 상태
  const [remainingTime, setRemainingTime] = useState(null);
  const [expiryIntervalId, setExpiryIntervalId] = useState(null);
  const navigate = useNavigate();

  // ✅ 로그인 시 토큰 저장 + 만료 감시 타이머 설정
  const login = (newToken) => {
    //console.log("[LOGIN] 호출됨");

    if (expiryTimeoutId) {
      //console.log("[LOGIN] 기존 timeout 제거");
      clearTimeout(expiryTimeoutId);
    }
    if (expiryIntervalId) {
      //console.log("[LOGIN] 기존 interval 제거");
      clearInterval(expiryIntervalId);
    }
    setExpiryTimeoutId(null);
    setExpiryIntervalId(null);
    setShowExpiryModal(false);
    setRemainingTime(null);

    sessionStorage.setItem("token", newToken);
    localStorage.setItem("token", newToken);
    setToken(newToken);
    watchTokenExpiry(newToken);
  };

  const logout = async () => {
    if (token) {
      try {
        await fetch(`${Server_IP}/api/show/logout`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      } catch (e) {
        // logout logging is best-effort only
      }
    }

    //console.log("[LOGOUT] 호출됨");

    sessionStorage.removeItem("token");
    localStorage.removeItem("token");
    setToken(null);
    setUserId(null);
    setUserType("client");
    setManagerName("");
    setManagerEmail("");
    setRemainingTime(null);
    setShowExpiryModal(false);

    if (expiryTimeoutId) {
      //console.log("[LOGOUT] timeout 제거");
      clearTimeout(expiryTimeoutId);
    }
    if (expiryIntervalId) {
      //console.log("[LOGOUT] interval 제거");
      clearInterval(expiryIntervalId);
    }
    setExpiryTimeoutId(null);
    setExpiryIntervalId(null);

    navigate("/login");
  };

  // ✅ 토큰 만료 감시 함수
  const watchTokenExpiry = (token) => {
    //console.log("[WATCH] 실행됨");

    if (expiryTimeoutId) {
      //console.log("[WATCH] 기존 timeout 제거");
      clearTimeout(expiryTimeoutId);
    }
    if (expiryIntervalId) {
      //console.log("[WATCH] 기존 interval 제거");
      clearInterval(expiryIntervalId);
    }

    try {
      const decoded = jwtDecode(token);

      // 기본 지속시간: 모든 사용자에 대해 1시간
      const baseDuration = 1 * 3600000;

      // JWT 만료시간(밀리초)
      const jwtExpiresAt = decoded?.exp ? decoded.exp * 1000 : null;

      // 실제 만료 대상: 토큰 만료와 baseDuration 중 빠른 쪽으로 설정
      const expiresAtCandidate = Date.now() + baseDuration;
      const expiresAt = jwtExpiresAt
        ? Math.min(jwtExpiresAt, expiresAtCandidate)
        : expiresAtCandidate;

      const timeRemaining = expiresAt - Date.now();

      if (timeRemaining <= 0) {
        setShowExpiryModal(true);
        logout();
        return;
      }

      const timeoutId = setTimeout(() => {
        setShowExpiryModal(true);
      }, timeRemaining);

      // 초기값 즉시 설정하여 렌더링 시 빈 상태/겹침 방지
      const initDiff = expiresAt - Date.now();
      if (initDiff > 0) {
        const ih = String(Math.floor(initDiff / 3600000)).padStart(2, "0");
        const im = String(Math.floor((initDiff % 3600000) / 60000)).padStart(
          2,
          "0",
        );
        const is = String(Math.floor((initDiff % 60000) / 1000)).padStart(
          2,
          "0",
        );
        setRemainingTime(`${ih}:${im}:${is}`);
      }

      const intervalId = setInterval(() => {
        const now = Date.now();
        const diff = expiresAt - now;

        if (diff <= 0) {
          setRemainingTime("만료됨");
          clearInterval(intervalId);
        } else {
          const h = String(Math.floor(diff / 3600000)).padStart(2, "0");
          const m = String(Math.floor((diff % 3600000) / 60000)).padStart(
            2,
            "0",
          );
          const s = String(Math.floor((diff % 60000) / 1000)).padStart(2, "0");
          setRemainingTime(`${h}:${m}:${s}`);
        }
      }, 1000);

      setExpiryTimeoutId(timeoutId);
      setExpiryIntervalId(intervalId);
    } catch (err) {
      console.error("[WATCH] 디코딩 실패:", err);
      logout();
    }
  };

  // ✅ 앱 시작 시 저장된 토큰 확인 (watchTokenExpiry 제거!)
  useEffect(() => {
    const storedToken =
      sessionStorage.getItem("token") || localStorage.getItem("token");
    const queryToken = new URLSearchParams(window.location.search).get(
      "authToken",
    );
    const queryUserType = new URLSearchParams(window.location.search).get(
      "userType",
    );
    const queryUserId = new URLSearchParams(window.location.search).get(
      "userId",
    );
    const queryManagerName = new URLSearchParams(window.location.search).get(
      "managerName",
    );
    const queryManagerEmail = new URLSearchParams(window.location.search).get(
      "managerEmail",
    );

    const hydrateTokenFromQuery = (incomingToken) => {
      sessionStorage.setItem("token", incomingToken);
      localStorage.setItem("token", incomingToken);
      setToken(incomingToken);

      if (queryUserType) {
        setUserType(queryUserType);
      }
      if (queryUserId) {
        setUserId(queryUserId);
      }
      if (queryManagerName) {
        setManagerName(queryManagerName);
      }
      if (queryManagerEmail) {
        setManagerEmail(queryManagerEmail);
      }

      watchTokenExpiry(incomingToken);

      const cleanUrl = `${window.location.pathname}${window.location.hash || ""}`;
      window.history.replaceState({}, "", cleanUrl);
    };

    if (!storedToken && queryToken) {
      try {
        const decoded = jwtDecode(queryToken);
        if (decoded.exp * 1000 <= Date.now()) {
          sessionStorage.removeItem("token");
          localStorage.removeItem("token");
          navigate("/login");
          setIsInitializing(false);
          return;
        }

        hydrateTokenFromQuery(queryToken);
        setIsInitializing(false);
        return;
      } catch (err) {
        sessionStorage.removeItem("token");
        localStorage.removeItem("token");
        navigate("/login");
        setIsInitializing(false);
        return;
      }
    }

    if (!storedToken) {
      navigate("/login");
      setIsInitializing(false);
    } else {
      try {
        const decoded = jwtDecode(storedToken);
        const expiresAt = decoded.exp * 1000;
        if (expiresAt <= Date.now()) {
          // 토큰 만료됨
          sessionStorage.removeItem("token");
          localStorage.removeItem("token");
          setToken(null);
          setUserId(null);
          setUserType("client");
          setManagerName("");
          setManagerEmail("");
          navigate("/login");
        } else {
          setToken(storedToken);
          watchTokenExpiry(storedToken);
        }
      } catch (err) {
        sessionStorage.removeItem("token");
        localStorage.removeItem("token");
        setToken(null);
        setUserId(null);
        setUserType("client");
        setManagerName("");
        setManagerEmail("");
        navigate("/login");
      }
      setIsInitializing(false);
    }
  }, []); // 의존성 배열을 빈 배열로 수정하여 최초 한 번만 실행

  const saveUserId = (id) => {
    setUserId(id);
  };

  // ✅ 모달에서 "확인" 눌렀을 때 처리
  const handleModalConfirm = () => {
    setShowExpiryModal(false);
    logout(); // 로그아웃 실행
  };

  if (isInitializing) {
    return null; // ✅ 토큰 확인 전에는 렌더링을 차단하여 화면 깜빡임(Flash) 방지
  }

  return (
    <AuthContext.Provider
      value={{
        Server_IP,
        token,
        isLoggedIn: !!token,
        isInitializing,
        login,
        logout,
        userId,
        saveUserId,
        showExpiryModal,
        setShowExpiryModal,
        handleModalConfirm,
        remainingTime,
        setUserId,
        userType,
        setUserType,
        managerName,
        setManagerName,
        managerEmail,
        setManagerEmail,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
